import React, { useCallback, useContext, useLayoutEffect, useMemo } from 'react';
import TaskContext from '@dyl/pages/Tasks/edit/TaskContext';
import { Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { kebabCase, snakeCase, times } from 'lodash';
import ImagePicker from '@dyl/components/ImagePicker';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import { useImages } from '@dyl/systems/ImagesManager';
import InlineTextButton from '@dyl/components/InlineTextButton';
import ReadMore from '@dyl/components/ReadMore';

const useStyles = makeStyles(theme => ({
    header: {
        marginBottom: theme.spacing(2),
    },
    description: {
        fontWeight: theme.typography.fontWeightBold,
        marginBottom: theme.spacing(2),
        maxWidth: theme.breakpoints.values.md,
    },
    imagesGroup: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',

        marginBottom: theme.spacing(3),

        '&>:not(:last-child)': {
            marginRight: theme.spacing(1),
        },
    },
}));

export default function TaskBackgroundImagesBlock() {
    const { taskType } = useContext(TaskContext);
    const classes = useStyles();

    const { count, description } = taskType?.backgroundImages ?? {};

    if (!taskType.backgroundImages) {
        return null;
    }

    return (
        <>
            <Typography variant='h6' className={classes.header}>
                Background Image{count !== 1 ? 's' : ''}
            </Typography>

            {!!description && (
                <Typography variant='body2' className={classes.description}>
                    {description}
                </Typography>
            )}

            <Box flexDirection='row' className={classes.imagesGroup}>
                {times(count, index => (
                    <Item key={index} index={index} />
                ))}
            </Box>
        </>
    );
}

function Item({ index }) {
    const { taskType, isNew } = useContext(TaskContext);
    const { configs } = taskType.backgroundImages;

    const name = `Background Image${index > 0 ? ` ${index + 1}` : ''}`;

    const [imageId, setImageId, apiImageError] = useTaskField(snakeCase(name));

    const {
        label,
        helperText,
        default: defaultId,
    } = configs?.[index] ?? {};

    const { images, imagesStatus } = useImages();

    const statusOfDefault = useMemo(() => {
        if (!defaultId) return 'unset';
        if (imagesStatus !== 'complete') return 'unknown';

        if (!images.some(image => image.image_id === defaultId)) return 'invalid';
        return 'valid';
    }, [imagesStatus, images, defaultId]);

    // If new, unset, and has valid default, auto-set default.
    useLayoutEffect(() => {
        if (!isNew || statusOfDefault !== 'valid') return;

        setImageId(prev => prev || defaultId);
    }, [defaultId, statusOfDefault]);

    const doDefaultMismatchAutoFix = useCallback(() => setImageId(defaultId), [defaultId]);

    const imageError = (
        ((statusOfDefault === 'invalid') && (
            <>
                Default image "{defaultId}" not found.{' '}
                <ReadMore title='Default file missing'>
                    <p>The default file, "{defaultId}", could not be found
                    in the database.</p>
                    <p>It is recommended that you contact a developer to fix the issue,
                    however you can take the following actions in an emergency:</p>
                    <ul>
                        <li>Re-upload the asset if you have it
                            or can request it from a co-worker.</li>
                        <li>Search for another suitable asset
                            using the image picker.</li>
                    </ul>
                </ReadMore>
            </>
        ))
        || ((statusOfDefault === 'valid' && (defaultId !== imageId)) && (
            <>
                Image does not match intended default: "{defaultId}".{' '}
                <ReadMore
                    title='Image does not match intended default'
                    actions={[{ text: 'Auto-Fix', onClick: doDefaultMismatchAutoFix }]}
                >
                    <p>The selected file does not match the intended default, "{defaultId}".</p>
                    <p>Please select the correct file, or use the auto-fix option below.</p>
                    <p>If you believe this message is being shown in error,
                        please contact a developer to resolve the issue.</p>
                </ReadMore>
                {' '}or <InlineTextButton onClick={doDefaultMismatchAutoFix}>auto-fix</InlineTextButton>.
            </>
        ))
        || apiImageError
        || ''
    );

    return (
        <ImagePicker
            id={kebabCase(name)}
            value={imageId ?? ''}
            onChange={setImageId}
            label={label ?? name}
            helperText={imageError || helperText}
            error={!!imageError}
            disabled={
                (statusOfDefault === 'valid' && defaultId === imageId)
                || statusOfDefault === 'unknown'
            }
        />
    );
}